import React, { useState, useEffect } from 'react';
import { Calendar, Clock, User, Building2, FileText, Activity } from 'lucide-react';
import ApiHandlerSuperCoordinator from './apiHandlerSuperCoordinator';

const AutoAssings = () => {
  const [formData, setFormData] = useState({
    institution: '',
    modality: '',
    bodyPart: '',
    radiologist: '',
    startTime: '',
    endTime: ''
  });

  const [dropdownData, setDropdownData] = useState({
    institutions: [],
    modalities: [],
    bodyParts: [],
    radiologist: []
  });

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  const api = new ApiHandlerSuperCoordinator();

  useEffect(() => {
    fetchDropdownData();
  }, []);

  const fetchDropdownData = async () => {
    try {
      setLoading(true);
      const [institutions, modalities, bodyParts, radiologist] = await Promise.all([
        api.getAllInstitutions(),
        api.getAllModalities(),
        api.makeRequest('/body-parts/'),
        api.getAllRadiologists()
      ]);

      setDropdownData({
        institutions: institutions?.institutions || [],
        modalities: Array.isArray(modalities) ? modalities : [],
        bodyParts: bodyParts || [],
        radiologist: radiologist || []
      });
    } catch (error) {
      console.error('Error fetching dropdown data:', error);
      setMessage({ type: 'error', text: 'Failed to load form data' });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async () => {
    // Validation
    if (!formData.institution || !formData.modality || !formData.bodyPart || 
        !formData.radiologist || !formData.startTime || !formData.endTime) {
      setMessage({ type: 'error', text: 'Please fill in all fields' });
      return;
    }

    if (formData.startTime >= formData.endTime) {
      setMessage({ type: 'error', text: 'End time must be after start time' });
      return;
    }

    try {
      setSubmitting(true);
      setMessage({ type: '', text: '' });

      // Submit the auto-assign data
      const response = await api.makeRequest('/create_auto_assign_rule/', {
        method: 'POST',
        body: JSON.stringify(formData)
      });

      setMessage({ type: 'success', text: 'Auto-assign settings saved successfully!' });
      
      // Reset form
      setFormData({
        institution: '',
        modality: '',
        bodyPart: '',
        radiologist: '',
        startTime: '',
        endTime: ''
      });
    } catch (error) {
      console.error('Error submitting form:', error);
      setMessage({ type: 'error', text: 'Failed to save auto-assign settings' });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto">
        <div className="bg-white rounded-lg shadow-md p-6 md:p-8">
          <div className="mb-6">
            <h1 className="text-3xl font-bold text-gray-900">Auto Assign</h1>
            <p className="mt-2 text-gray-600">Configure automatic assignment settings for radiologist</p>
          </div>

          {message.text && (
            <div className={`mb-6 p-4 rounded-lg ${
              message.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' : 
              'bg-red-50 text-red-800 border border-red-200'
            }`}>
              {message.text}
            </div>
          )}

          <div className="space-y-6">
            {/* Institution */}
            <div>
              <label htmlFor="institution" className="block text-sm font-medium text-gray-700 mb-2">
                <Building2 className="inline w-4 h-4 mr-2" />
                Institution
              </label>
              <select
                id="institution"
                name="institution"
                value={formData.institution}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition"
              >
                <option value="">Select Institution</option>
                {dropdownData.institutions.map((inst, index) => (
                  <option key={index} value={inst}>
                    {inst}
                  </option>
                ))}
              </select>
            </div>

            {/* Modality */}
<div>
  <label htmlFor="modality" className="block text-sm font-medium text-gray-700 mb-2">
    <Activity className="inline w-4 h-4 mr-2" />
    Modality
  </label>
  <select
    id="modality"
    name="modality"
    value={formData.modality}
    onChange={handleInputChange}
    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition"
  >
    <option value="">Select Modality</option>
    {dropdownData.modalities.map((modality, index) => (
      <option key={index} value={modality}>
        {modality}
      </option>
    ))}
  </select>
</div>


            {/* Body Part */}
            <div>
              <label htmlFor="bodyPart" className="block text-sm font-medium text-gray-700 mb-2">
                <FileText className="inline w-4 h-4 mr-2" />
                Body Part
              </label>
              <select
                id="bodyPart"
                name="bodyPart"
                value={formData.bodyPart}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition"
              >
                <option value="">Select Body Part</option>
                {dropdownData.bodyParts.map((part) => (
                  <option key={part.id} value={part.id}>
                    {part.name || part.body_part_name}
                  </option>
                ))}
              </select>
            </div>

            {/* Radiographer */}
            <div>
              <label htmlFor="radiographer" className="block text-sm font-medium text-gray-700 mb-2">
                <User className="inline w-4 h-4 mr-2" />
                radiologist
              </label>
              <select
                id="radiologist"
                name="radiologist"
                value={formData.radiologist}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition"
              >
                <option value="">Select radiologist</option>
                {dropdownData.radiologist.map((radio) => (
                  <option key={radio.id} value={radio.id}>
                    {radio.name || radio.first_name + ' ' + radio.last_name}
                  </option>
                ))}
              </select>
            </div>

            {/* Start Time */}
            <div>
              <label htmlFor="startTime" className="block text-sm font-medium text-gray-700 mb-2">
                <Clock className="inline w-4 h-4 mr-2" />
                Start Time
              </label>
              <input
                type="time"
                id="startTime"
                name="startTime"
                value={formData.startTime}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition"
              />
            </div>

            {/* End Time */}
            <div>
              <label htmlFor="endTime" className="block text-sm font-medium text-gray-700 mb-2">
                <Clock className="inline w-4 h-4 mr-2" />
                End Time
              </label>
              <input
                type="time"
                id="endTime"
                name="endTime"
                value={formData.endTime}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition"
              />
            </div>

            {/* Submit Button */}
            <div className="pt-4">
              <button
                onClick={handleSubmit}
                disabled={submitting}
                className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                {submitting ? 'Submitting...' : 'Submit'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AutoAssings;